/**
 * Iter-157A Phase 5 — The Attic timeline.
 *
 * The Attic is a TRUST surface, not a workflow surface. Producers come
 * here for reassurance — "everything is here, nothing is lost, you can
 * always get back" — not to perform a task. Every visual choice here
 * is calibrated for that emotional contract.
 *
 * Presentation rules baked into this component:
 *
 *   • Time is the spine. Rows group by calendar day, headed by a
 *     gentle "Today / Yesterday / Mon 9 Feb" label so the eye walks
 *     the project's life chronologically.
 *
 *   • Trigger noise is collapsed into THREE human categories — Manual
 *     (★), System (◆ — the auto.before_* family), Hourly (●). Producers
 *     never need to know the underlying enum.
 *
 *   • System-critical rows feel IMPORTANT but never ALARMING. A soft
 *     amber accent (not red) communicates "anchor", not "warning".
 *
 *   • Captions explain WHAT happened in plain English, derived from
 *     the trigger + label. Technical metadata (hash, seq, byte size)
 *     is intentionally absent at this level — the preview drawer is
 *     where forensic curiosity gets satisfied.
 *
 *   • The active row (when the preview drawer is open) lights up with
 *     a delicate ring, never a heavy background, so the timeline keeps
 *     reading as a continuous thread.
 */

import { Star, Diamond, Circle, Pin } from "lucide-react";


// Human captions per trigger. Used both for the row title and the
// preview drawer's "From" header so producers see the same words
// everywhere. Order of preference: explicit label set by the user /
// system > fallback caption derived from the trigger constant.
const TRIGGER_CAPTION = {
    "manual": "Manual restore point",
    "auto.hourly": "Hourly snapshot",
    "auto.before_archive": "Before archive",
    "auto.before_unarchive": "Before unarchive",
    "auto.before_ownership_transfer": "Before ownership transfer",
    "auto.before_script_upload": "Before script upload",
    "auto.before_script_parse": "Before script parse",
    "auto.before_revision_import": "Before revision import",
    "auto.before_bulk_edit": "Before bulk edit",
    "auto.before_restore": "Safety snapshot before restore",
    "auto.after_restore": "Snapshot after restore",
};


function captionFor(row) {
    return (row.label || "").trim() || TRIGGER_CAPTION[row.trigger] || "Restore point";
}


function categoryFor(row) {
    if (row.trigger === "manual") return "manual";
    if (row.trigger === "auto.hourly") return "hourly";
    return "system"; // every auto.before_* + auto.after_restore
}


function timeOfDay(iso) {
    if (!iso) return "";
    try {
        return new Date(iso).toLocaleTimeString(undefined, {
            hour: "2-digit",
            minute: "2-digit",
        });
    } catch (_) { return ""; }
}


function dayKey(iso) {
    if (!iso) return "unknown";
    try {
        const d = new Date(iso);
        return new Date(d.getFullYear(), d.getMonth(), d.getDate()).toISOString();
    } catch (_) { return iso; }
}


function dayLabel(iso) {
    if (!iso) return "Earlier";
    try {
        const d = new Date(iso);
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const day = new Date(d.getFullYear(), d.getMonth(), d.getDate());
        const diffDays = Math.round((today - day) / 86400000);
        if (diffDays === 0) return "Today";
        if (diffDays === 1) return "Yesterday";
        if (diffDays < 7) {
            return day.toLocaleDateString(undefined, {
                weekday: "long",
            });
        }
        return day.toLocaleDateString(undefined, {
            day: "numeric",
            month: "short",
            year: day.getFullYear() === today.getFullYear() ? undefined : "numeric",
        });
    } catch (_) { return iso; }
}


function CategoryGlyph({ kind }) {
    if (kind === "manual") {
        return (
            <span
                className="flex h-7 w-7 items-center justify-center rounded-full border border-amber-300/40 bg-amber-300/10 text-amber-200"
                data-testid="attic-glyph-manual"
                aria-label="Manual restore point"
            >
                <Star size={13} strokeWidth={2.2} fill="currentColor" />
            </span>
        );
    }
    if (kind === "system") {
        return (
            <span
                className="flex h-7 w-7 items-center justify-center rounded-full border border-amber-200/30 bg-amber-200/[0.06] text-amber-200/90"
                data-testid="attic-glyph-system"
                aria-label="System safety snapshot"
            >
                <Diamond size={12} strokeWidth={2.2} />
            </span>
        );
    }
    return (
        <span
            className="flex h-7 w-7 items-center justify-center rounded-full border border-white/10 bg-white/[0.03] text-neutral-500"
            data-testid="attic-glyph-hourly"
            aria-label="Hourly snapshot"
        >
            <Circle size={9} strokeWidth={0} fill="currentColor" />
        </span>
    );
}


function TimelineRow({ row, isActive, onSelect }) {
    const caption = captionFor(row);
    const cat = categoryFor(row);
    const captureBy = (row.created_by_name || "").trim();
    return (
        <button
            type="button"
            data-testid={`attic-rp-${row.id}`}
            data-trigger={row.trigger}
            data-category={cat}
            onClick={() => onSelect(row)}
            className={[
                "group relative w-full text-left rounded-lg border px-3 py-3 transition",
                "flex gap-3 items-start",
                isActive
                    ? "border-amber-200/40 bg-amber-200/[0.04] ring-1 ring-amber-200/30"
                    : "border-white/[0.06] bg-white/[0.015] hover:bg-white/[0.035] hover:border-white/[0.12]",
            ].join(" ")}
        >
            <CategoryGlyph kind={cat} />
            <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 text-sm">
                    <span className="font-medium text-neutral-100 truncate">{caption}</span>
                    {row.pinned ? (
                        <span
                            className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.18em] text-amber-200/80"
                            data-testid="attic-row-pinned"
                            title="Pinned — never auto-pruned"
                        >
                            <Pin size={10} strokeWidth={2.4} /> Pinned
                        </span>
                    ) : null}
                </div>
                <div className="mt-0.5 text-[11px] text-neutral-500 flex flex-wrap items-center gap-x-2">
                    <span data-testid="attic-row-time">{timeOfDay(row.created_at)}</span>
                    {captureBy ? <span>· {captureBy}</span> : null}
                    {row.reason ? (
                        <span className="italic text-neutral-400 truncate">
                            · &ldquo;{row.reason}&rdquo;
                        </span>
                    ) : null}
                </div>
            </div>
        </button>
    );
}


export default function AtticTimeline({ rows, activeId, onSelect }) {
    if (!rows || rows.length === 0) return null;
    // Group by day, preserving newest-first order.
    const groups = [];
    let currentKey = null;
    let bucket = null;
    for (const r of rows) {
        const k = dayKey(r.created_at);
        if (k !== currentKey) {
            currentKey = k;
            bucket = { key: k, label: dayLabel(r.created_at), rows: [] };
            groups.push(bucket);
        }
        bucket.rows.push(r);
    }

    return (
        <div className="space-y-7" data-testid="attic-timeline">
            {groups.map((g) => (
                <section key={g.key} data-testid={`attic-group-${g.key}`}>
                    <h3 className="text-[11px] uppercase tracking-[0.28em] text-neutral-500 mb-2 pl-1">
                        {g.label}
                    </h3>
                    <ul className="space-y-1.5">
                        {g.rows.map((r) => (
                            <li key={r.id}>
                                <TimelineRow
                                    row={r}
                                    isActive={activeId === r.id}
                                    onSelect={onSelect}
                                />
                            </li>
                        ))}
                    </ul>
                </section>
            ))}
        </div>
    );
}
